import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { IThemeContext, IThemeResponse } from './custom-theme-context.model';
import gaiaTheme from '../../root-components/theme';
import OrganisationService from '../../shared/services/organisation/organisation.service';
import { UserDetailsContext } from '../user-details/user-details';
import { createTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { darken, lighten } from '@mui/system';
import gaiaLogoUrl from '../../assets/corporate-logos/gaia/gaia-logo-no-padding.png';
import { MetaTagProvider } from '../../root-components/meta-tag-provider';
const { palette } = createTheme();

export const CustomThemeContext = createContext<IThemeContext>({} as IThemeContext);

export const CustomThemeProvider = ({ children }: PropsWithChildren) => {

  const organisationService = new OrganisationService()
  const userDetailsContext = useContext(UserDetailsContext)

  const [theme, setTheme] = useState(gaiaTheme);
  const [partnerName, setPartnerName] = useState('Gaia');
  const [baseTheme, setBaseTheme] = useState(gaiaTheme)
  const [logoUrl, setLogoUrl] = useState(gaiaLogoUrl);
  const [logoPosition, setLogoPosition] = useState('start');
  const [hideSignUpButton, setHideSignUpButton] = useState(false);
  const [partnerLoginHeadline, setPartnerLoginHeadline] = useState('');
  const [partnerLoginSubheadline, setPartnerLoginSubheadline] = useState('');
  const [partnerLoginEyebrow, setPartnerLoginEyebrow] = useState('');
  const [partnerReversedLogoFilepath, setPartnerReversedLogoFilepath] = useState('');
  const [isBrandReady, setIsBrandReady] = useState(false);

  useEffect(() => {
    const baseUrl = window.location.origin;
    // Gaia / localhost: we know branding synchronously, so mark brand as ready immediately
    if (baseUrl.indexOf('gaiacompany.io') !== -1 || baseUrl.indexOf('localhost') !== -1) {
      setTheme(gaiaTheme);
      setBaseTheme(gaiaTheme);
      setPartnerName('Gaia');
      setLogoUrl(gaiaLogoUrl);
      setLogoPosition('start');
      setHideSignUpButton(false);
      setPartnerLoginHeadline('');
      setPartnerLoginSubheadline('');
      setPartnerLoginEyebrow('');
      setPartnerReversedLogoFilepath('');
      setIsBrandReady(true);
      return;
    }

    // Custom / partner domains: fetch branding before allowing the app to render
    organisationService.getOrganisationByCustomDomainUrl(baseUrl)
      .then(response => {
        if (response.partnerColor) {
          const mergedTheme = mergePartnerTheme(response);
          setTheme(mergedTheme);
          setBaseTheme(mergedTheme);
        } else {
          setTheme(gaiaTheme);
          setBaseTheme(gaiaTheme);
        }

        if (response.partnerName) {
          setPartnerName(response.partnerName);
        } else {
          setPartnerName('Gaia');
        }

        if (response.partnerLogoFilepath) {
          setLogoUrl(response.partnerLogoFilepath);
        } else {
          setLogoUrl(gaiaLogoUrl);
        }

        if (response.logoPosition) {
          setLogoPosition(response.logoPosition);
        } else {
          setLogoPosition('start');
        }

        if (response.hideSignUpButton) {
          setHideSignUpButton(response.hideSignUpButton);
        } else {
          setHideSignUpButton(false);
        }

        setPartnerLoginHeadline(response.partnerLoginHeadline || '');
        setPartnerLoginSubheadline(response.partnerLoginSubheadline || '');
        setPartnerLoginEyebrow(response.partnerLoginEyebrow || '');
        setPartnerReversedLogoFilepath(response.partnerReversedLogoFilepath || '');

        if (response.partnerIconFilepath) {
          const link: any = document.querySelector("link[rel*='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'icon';
          link.href = response.partnerIconFilepath;

          document.head.appendChild(link);
        }

        setIsBrandReady(true);
      })
      .catch(() => {
        // On error, fall back to Gaia branding but still allow the app to render
        setTheme(gaiaTheme);
        setBaseTheme(gaiaTheme);
        setPartnerName('Gaia');
        setLogoUrl(gaiaLogoUrl);
        setLogoPosition('start');
        setHideSignUpButton(false);
        setPartnerLoginHeadline('');
        setPartnerLoginSubheadline('');
        setPartnerLoginEyebrow('');
        setPartnerReversedLogoFilepath('');
        setIsBrandReady(true);
      });
  // We intentionally only care about the initial origin here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userDetailsContext.userDetails?.organisation.theme) {
      setTheme(userDetailsContext.userDetails?.organisation.theme)
      const isLoggedInAsClient = userDetailsContext.userDetails?.isLoggedInAsClient;
      const partnerColor = userDetailsContext.userDetails?.partnerOrganisation?.partnerColor;
      if (isLoggedInAsClient && partnerColor) {
        sessionStorage.setItem('partnerColor', partnerColor)
      } else {
        sessionStorage.removeItem('partnerColor')
      }
    } else {
      setTheme(baseTheme)
      sessionStorage.removeItem('partnerColor')
    }
  }, [userDetailsContext.userDetails, baseTheme]);

  useEffect(() => {
    const partnerColor = sessionStorage.getItem('partnerColor');
    const isLoggedInAsClient = userDetailsContext.userDetails?.isLoggedInAsClient;
    if (!isLoggedInAsClient) {
      sessionStorage.removeItem('partnerColor');
      return;
    }
    if (partnerColor) {
      setTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          primary: { ...baseTheme.palette.primary, main: partnerColor, light: lighten(partnerColor, 0.6), dark: darken(partnerColor, 0.4) },
          secondary: { ...baseTheme.palette.secondary, main: lighten(partnerColor, 0.6), light: lighten(partnerColor, 0.8), dark: darken(partnerColor, 0.4) }
        }
      });
    }
  }, [userDetailsContext.userDetails, baseTheme])


  function resetToBaseTheme(): void {
    if (baseTheme) {
      setTheme(baseTheme)
    } else {
      setTheme(gaiaTheme)
    }
  };

  const mergePartnerTheme = (response: IThemeResponse) => {
    const partnerColor = response?.partnerColor;
    if (partnerColor) {
      const merged = deepmerge(gaiaTheme, createTheme({
        components: {
          MuiButton: {
            defaultProps: {
              disableRipple: true,
              color: 'primary',
              variant: 'contained',
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
            variants: [
              {
                props: { variant: 'text' },
                style: {
                  backgroundColor: 'white',
                  color: 'primary.main'
                }
              }
            ]
          },
          MuiTab: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              root: {
                textTransform: 'capitalize',
              }
            }
          }
        },
        typography: {
          fontFamily: [
            'EffraMd',
            'EffraRg',
            'Effra',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif'
          ].join(','),
          body1: {
            fontFamily: [
              'EffraRg',
              'EffraMd',
              'Effra',
              'Roboto',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif'
            ].join(',')
          }
        },
        palette: {
          // Target WCAG AA when auto-picking on-primary text so a light partner
          // primary gets dark (legible) button text instead of white-on-light.
          contrastThreshold: 4.5,
          primary: {
            main: partnerColor,
            light: lighten(partnerColor, 0.6),
            // contrastText intentionally omitted: createTheme's augmentColor
            // computes an accessible value from the partner primary (stays white
            // for dark primaries, flips to dark for light ones).
          },
          secondary: {
            main: lighten(partnerColor, 0.6),
            light: lighten(partnerColor, 0.8),
          },
          lightGrey: palette.augmentColor({
            color: {
              main: '#e7e7e7',
              light: '#f1f1f1',
              dark: '#0000001f'
            }
          }),
          text: {
            primary: "#111E21"
          },
          error: {
            main: '#f44336',
            light: '#f6685e',
            dark: '#aa2e25',
          },
          warning: {
            main: '#ff9800',
            light: '#ffb74d',
            dark: '#f57c00',
            contrastText: '#fff',
          },
        },
        shape: {
          borderRadius: 10
        }
      }));
      return merged;
    } else {
      return gaiaTheme;
    }
  };
  return (
  <CustomThemeContext.Provider value={{ theme, setTheme, logoUrl, resetToBaseTheme, partnerName, hideSignUpButton, partnerLoginHeadline, partnerLoginSubheadline, partnerLoginEyebrow, partnerReversedLogoFilepath, logoPosition: logoPosition as "start" | "center" | "end" }}>
      {isBrandReady && <>
        <MetaTagProvider
          metaTitle={`Carbon Accounting`}
          metaDescription={`The ${partnerName} Carbon Accounting platform is the world's leading software for any company or corporation looking to track and report on their Carbon Emissions.`} />
        {children}
      </>}
    </CustomThemeContext.Provider>
  );
};

export const useTheme = () => useContext(CustomThemeContext);
